import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { GapQuestionThread } from '../shared/GapQuestionThread';
import { type GapDomain, type GapQuestion } from '../../utils/patientGapAnalysisStore';

interface GapAnalysisQuestionCardProps {
  analysisId: string;
  question: GapQuestion;
  domainColors: Record<GapDomain, 'error' | 'warning' | 'info' | 'success' | 'default'>;
  onShareChange: (analysisId: string, questionId: string, checked: boolean) => void;
  onAddComment: (analysisId: string, questionId: string, comment: string) => void;
  onEditQuestion: (analysisId: string, questionId: string, updates: { question: string; rationale: string; domain: GapDomain }) => void;
  onDeleteQuestion: (analysisId: string, questionId: string) => void;
}

export const GapAnalysisQuestionCard = ({
  analysisId,
  question,
  domainColors,
  onShareChange,
  onAddComment,
  onEditQuestion,
  onDeleteQuestion,
}: GapAnalysisQuestionCardProps) => {
  const [comment, setComment] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [draftQuestion, setDraftQuestion] = useState(question.question);
  const [draftRationale, setDraftRationale] = useState(question.rationale);
  const [draftDomain, setDraftDomain] = useState<GapDomain>(question.domain);

  const isArchived = question.patientStatus === 'ANSWERED';
  const sourceDetails = [
    question.sourceTimestamps?.cgmDataUpdatedAt ? `CGM ${new Date(question.sourceTimestamps.cgmDataUpdatedAt).toLocaleString()}` : null,
    question.sourceTimestamps?.nutritionDataUpdatedAt ? `Nutrition ${new Date(question.sourceTimestamps.nutritionDataUpdatedAt).toLocaleString()}` : null,
    question.sourceTimestamps?.lastMealLoggedAt ? `Last meal ${new Date(question.sourceTimestamps.lastMealLoggedAt).toLocaleString()}` : null,
    question.sourceTimestamps?.carePlanUpdatedAt ? `Care plan ${new Date(question.sourceTimestamps.carePlanUpdatedAt).toLocaleDateString()}` : null,
    question.sourceTimestamps?.latestGoalTargetDate ? `Latest goal target ${new Date(question.sourceTimestamps.latestGoalTargetDate).toLocaleDateString()}` : null,
  ].filter(Boolean).join(' | ');

  const handleSubmitComment = () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      return;
    }

    onAddComment(analysisId, question.id, trimmed);
    setComment('');
  };

  const handleStartEditing = () => {
    setDraftQuestion(question.question);
    setDraftRationale(question.rationale);
    setDraftDomain(question.domain);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    onEditQuestion(analysisId, question.id, {
      question: draftQuestion.trim(),
      rationale: draftRationale.trim(),
      domain: draftDomain,
    });
    setIsEditing(false);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Chip
          label={question.severity}
          size="small"
          color={question.severity === 'HIGH' ? 'error' : question.severity === 'MEDIUM' ? 'warning' : 'default'}
          sx={{ mt: 0.5, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Chip label={question.gapLabel} size="small" color={domainColors[question.domain]} />
            <Chip label={question.resolutionStatus === 'RESOLVED' ? 'Resolved' : 'Still Open'} size="small" color={question.resolutionStatus === 'RESOLVED' ? 'success' : 'warning'} />
            {isArchived && <Chip label="Archived" size="small" variant="outlined" />}
            {question.lastUpdatedAt && (
              <Typography variant="caption" color="text.secondary">
                Last updated {new Date(question.lastUpdatedAt).toLocaleString()}
              </Typography>
            )}
            {!isArchived && (
              <>
                <IconButton size="small" onClick={isEditing ? () => setIsEditing(false) : handleStartEditing}>
                  {isEditing ? <CloseIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" color="error" onClick={() => onDeleteQuestion(analysisId, question.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Stack>
          {isEditing ? (
            <Stack spacing={1} sx={{ mb: 1 }}>
              <TextField
                select
                fullWidth
                label="Domain"
                value={draftDomain}
                onChange={(event) => setDraftDomain(event.target.value as GapDomain)}
              >
                {(['CLINICAL', 'BEHAVIORAL', 'CULTURAL', 'FAMILY', 'SDOH'] as GapDomain[]).map((domain) => (
                  <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Question"
                value={draftQuestion}
                onChange={(event) => setDraftQuestion(event.target.value)}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Rationale"
                value={draftRationale}
                onChange={(event) => setDraftRationale(event.target.value)}
              />
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveEdit}
                  disabled={!draftQuestion.trim() || !draftRationale.trim()}
                >
                  Save Question
                </Button>
              </Box>
            </Stack>
          ) : (
            <>
              <Typography variant="body2">{question.question}</Typography>
              {question.rationale && (
                <Typography variant="caption" color="text.secondary">
                  {question.rationale}
                </Typography>
              )}
            </>
          )}
          {sourceDetails && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              Evidence timestamps: {sourceDetails}
            </Typography>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Checkbox
              checked={Boolean(question.sharedWithPatient)}
              disabled={isArchived}
              onChange={(event) => onShareChange(analysisId, question.id, event.target.checked)}
            />
            <Typography variant="body2">Show on patient Questions page</Typography>
            {question.patientStatus && question.patientStatus !== 'UNSENT' && (
              <Chip
                label={question.patientStatus === 'PENDING_PATIENT' ? 'Awaiting patient response' : 'Answered'}
                size="small"
                color={question.patientStatus === 'ANSWERED' ? 'success' : 'warning'}
              />
            )}
          </Stack>

          <Box sx={{ mt: 1.5 }}>
            <GapQuestionThread
              thread={question.thread}
              emptyText="No patient or clinician conversation yet."
            />
          </Box>

          {!isArchived && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Clinician follow-up comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
              <Box>
                <Button variant="outlined" onClick={handleSubmitComment} disabled={!comment.trim()}>
                  Add Comment
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};